'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  type AllProductsQueryParams,
  type ProductListItem,
} from '@/common/types/product.type';
import classNames from 'classnames';
import { useGetStoreProductList } from '@/common/apis/storefronts/v1/get-product-list.api';
import EmptyState from '@/common/components/empty-state';
import { HybridPagination } from '@/common/components/hybrid-pagination/hybrid-pagination.component';
import Skeleton from '@/common/components/skeleton';
import Text from '@/common/components/text/text.component';
import createUrlUtility from '@/common/utils/create-url.utility';
import Product from './product.component';

export interface Props {
  queryParams: AllProductsQueryParams;
  hasCollection: boolean;
  collectionsLength: number;
  isLoadingCollection: boolean;
  vendorSlug: string;
  initialProducts?: ProductListItem[];
  initialTotalCount?: number;
}

export default function Products({
  queryParams,
  isLoadingCollection,
  vendorSlug,
  initialProducts = [],
  initialTotalCount = 0,
}: Props) {
  const searchParams = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;

  const [products, setProducts] = useState<ProductListItem[]>(initialProducts);
  const pageLoadMode = useRef<'append' | 'replace'>('append');

  const apiPageIndex = page;

  const {
    sortDirection,
    sortCondition,
    pageSize,
    categoryList,
    isAvailable,
    isDiscounted,
    searchByProductName,
  } = queryParams;

  const { data, isFetching, isSuccess, isLoading, isPlaceholderData } =
    useGetStoreProductList({
      params: {
        searchByProductName: searchByProductName || '',
        sortDirection,
        sortCondition,
        categoryList,
        isAvailable,
        isDiscounted,
        pageSize,
        pageIndex: apiPageIndex,
        vendorSlug,
      },
    });
  const isInitialLoading = page === 1 && isLoading;
  const totalItems = data?.data?.productCount ?? initialTotalCount;

  useLayoutEffect(() => {
    if (!data || isPlaceholderData) return;

    if (page === 1 || pageLoadMode.current === 'replace') {
      setProducts(data.data.items);
    } else {
      setProducts((prev) => [
        ...prev,
        ...data.data.items.filter(
          (item) => !prev.some((p) => p.id === item.id)
        ),
      ]);
    }
  }, [data, isPlaceholderData, page]);

  if (!products.length && !isLoading) {
    return (
      <EmptyState
        message={
          !products?.length
            ? 'محصولی یافت نشد !'
            : 'هنوز ویترینو نچیدیم، تا یه دور بزنی تمومه!'
        }
        iconSrc={createUrlUtility({
          params: 'static/images/svg/empty-state-product.svg',
        })}
        className={'!static !my-4'}
      />
    );
  }

  return (
    <HybridPagination
      currentPage={page}
      totalItems={totalItems}
      pageSize={pageSize}
      listLength={products.length} // data.length
      stopAtPage={5}
      isFetching={isFetching}
      onPageChange={(_, mode) => {
        pageLoadMode.current = mode;
      }}
    >
      <div
        className={classNames('relative h-full', {
          'flex items-center justify-center': !isSuccess && isInitialLoading,
        })}
      >
        {products.length > 0 && (
          <div className="flex flex-col">
            <div className="my-3 flex items-center justify-between text-xs text-secondary-300 lg:hidden">
              <Text className="font-semibold">همه محصولات</Text>
              <h2 className="font-normal">{`${totalItems}`} محصول</h2>
            </div>

            {/* SEO-friendly product list */}
            <section id="product-list" aria-labelledby="product-list-title">
              <h2 id="product-list-title" className="sr-only">
                لیست محصولات
              </h2>
              <ul className="grid grid-cols-[repeat(auto-fit,_minmax(150px,_1fr))] justify-start gap-4 md:grid-cols-[repeat(5,212px)] md:gap-[27px]">
                {products.map((item) => (
                  <li key={item.id} className="mb-3 box-border">
                    <Product itemDetail={item} vendorSlug={vendorSlug} />
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}

        {isInitialLoading ||
          (isFetching && isLoading && (
            <div className="my-4 flex items-center justify-center gap-4">
              <Skeleton type="product-item" />
              <Skeleton type="product-item" />
              <Skeleton type="product-item" />
              <Skeleton type="product-item" />
              <Skeleton type="product-item" />
            </div>
          ))}
      </div>
    </HybridPagination>
  );
}
